import { useCallback } from 'react';
import { toPostUI } from '../../../mappers/posts/toPostUI';
import { IconKebab } from '../../../ui/icon/IconKebab';
import { IconButton } from '../../../ui/input/IconButton';
import { Box } from '../../../ui/layout/Box';
import { DataTestId } from '../../../ui/layout/DataTestIdWrapper';
import { ResizableGrid } from '../../../ui/layout/ResizableGrid/ResizableGrid';
import { PostCardGrid } from '../../../ui/posts/PostCardGrid/PostCardGrid';
import { calculateGridCardHeight } from '../../../ui/posts/PostCardGrid/utils';
import { PostContextMenu } from '../../../ui/posts/PostContextMenu';
import { EventSource } from '../../../utils/analyticsService';
import { hasAvailableProducts } from '../../../utils/hasAvailableProducts';
import { useProductsPreviewEnabled } from '../../../utils/useProductsPreviewEnabled';
import { withPostCallbacks } from '../../post/withPostCallbacks';
import type { BusinessResizableGridProps } from './types';

const EnchancedPostGridCardCompact = withPostCallbacks(PostCardGrid, EventSource.BUSINESS);

const EnhancedPostContextMenu = withPostCallbacks(PostContextMenu, EventSource.BUSINESS);

export const BusinessResizableGrid: React.FC<BusinessResizableGridProps> = ({
    items,
    businessId,
    user,
    onLoadNext,
    onPostKebabClick,
}) => {
    const productsPreviewEnabled = useProductsPreviewEnabled();

    const getItemHeightCallback = useCallback(
        (index: number, width: number) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return 0;
            }

            return calculateGridCardHeight(width, toPostUI(postModel).content, postModel.postType, {
                withoutFooter: true,
                withProductsPreview: Boolean(
                    productsPreviewEnabled && hasAvailableProducts(postModel),
                ),
            });
        },
        [items, productsPreviewEnabled],
    );

    const renderItem = useCallback(
        (
            index: number,
            columns: number,
            isVisible?: boolean,
            cardSize?: { width: number; height: number },
        ) => {
            const postModel = items[index];

            if (!postModel || postModel.isAd) {
                return null;
            }

            const imageSize = cardSize && {
                width: cardSize.width * 1.5,
                height: cardSize.height * 1.5,
            };

            return (
                <EnchancedPostGridCardCompact
                    imageSize={imageSize}
                    post={postModel}
                    index={index}
                    gridColumns={columns}
                    isVisible={isVisible}
                    showFooter={false}
                    additionalIcon={
                        <EnhancedPostContextMenu
                            index={index}
                            trigger={
                                <IconButton
                                    backgroundColor="rgba(0, 0, 0, 0.24)"
                                    color="var(--control-secondary-blur-color)"
                                    shape="rect"
                                    size="xs"
                                >
                                    <IconKebab
                                        fontSize={16}
                                        dataTestId={DataTestId.POST_CARD_KEBAB}
                                    />
                                </IconButton>
                            }
                            onTriggerClick={onPostKebabClick}
                            post={postModel}
                            user={user}
                            fromBusinessId={businessId}
                        />
                    }
                    hideReportedPosts={false}
                    isCompact
                    isShowStatus
                    withProductsPreview={productsPreviewEnabled}
                />
            );
        },
        [items, onPostKebabClick, user, businessId, productsPreviewEnabled],
    );

    if (!items.length) {
        return null;
    }

    return (
        <Box p={2} background="var(--background-primary)">
            <ResizableGrid
                minItemWidth={210}
                items={items}
                loadMore={onLoadNext}
                getItemHeight={getItemHeightCallback}
                renderItem={renderItem}
            />
        </Box>
    );
};
